import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "@/lib/site";

const LINKS = [
  { href: "#probleme", label: "Le problème" },
  { href: "#solution", label: "Comment ça marche" },
  { href: "#services", label: "Formules" },
  { href: "#deductibilite", label: "Déductibilité" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#1A2E4A]/95 backdrop-blur-md border-b border-white/10"
          : "bg-[#1A2E4A]/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a
            href="#accueil"
            data-testid="header-logo"
            className="flex items-center gap-2 group"
            onClick={close}
          >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-[#E8611A] text-white font-display font-extrabold text-sm">
              DA
            </span>
            <span className="font-display font-bold text-white tracking-tight text-lg">
              {SITE.brand}
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-sm text-white/80 hover:text-white transition-colors font-medium"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${SITE.phone.replace(/\s/g, "")}`}
              data-testid="header-phone"
              className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              {SITE.phone}
            </a>
            <a
              href={SITE.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta-calendly"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-sm bg-[#E8611A] text-white text-sm font-semibold hover:bg-[#E8611A]/90 transition-colors"
            >
              Prendre rendez-vous
            </a>
          </div>

          <button
            type="button"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm text-white hover:bg-white/10 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      <div
        className={`lg:hidden fixed inset-x-0 top-16 sm:top-20 bottom-0 bg-[#1A2E4A] transition-all duration-300 ${
          open ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        data-testid="mobile-menu"
      >
        <div className="flex flex-col h-full px-6 py-8 gap-2 overflow-y-auto">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={close}
              data-testid={`mobile-nav-link-${l.href.slice(1)}`}
              className="py-3 text-white text-lg font-display font-semibold border-b border-white/10"
            >
              {l.label}
            </a>
          ))}
          <a
            href={SITE.calendly}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            data-testid="mobile-cta-calendly"
            className="mt-6 inline-flex items-center justify-center px-5 py-3.5 rounded-sm bg-[#E8611A] text-white text-base font-semibold"
          >
            Prendre rendez-vous
          </a>
          <a
            href={`tel:${SITE.phone.replace(/\s/g, "")}`}
            onClick={close}
            data-testid="mobile-phone"
            className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm border border-white/20 text-white text-base"
          >
            <Phone className="w-4 h-4" /> {SITE.phone}
          </a>
        </div>
      </div>
    </header>
  );
}
